import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddLinkIcon from '@mui/icons-material/AddLink';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useShopProxy } from './ShopDocumentImport';

/**
 * The shop's file library (assets/files): upload a file, get its URL.
 *
 * Replaces the legacy admin's "0 item(s)" uploader in the top bar. That one only
 * remembered uploads for the browser session that made them -- a colleague, or
 * the same user an hour later, saw an empty list again although the files were
 * still on the shop. Here the list is read from the shop itself
 * (mini-api GET /admin/files), so it is the same for everyone, always.
 *
 * The storefront opens any link containing `/files/` in a new tab, and prefixes
 * `http://` to URLs without a scheme, so the URL offered here is absolute.
 *
 * On a shop whose mini-api predates the list route, uploading still works; the
 * list then falls back to what was uploaded in this session, with a notice.
 */

const MAX_FILE_BYTES = 20 * 1024 * 1024;

const formatSize = (bytes) => {
    if (!Number.isFinite(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
});

const ShopFileLibrary = ({ t, onUseUrl }) => {
    const tr = (key, fallback) => {
        if (typeof t !== 'function') return fallback;
        const value = t(key);
        return (!value || value === key) ? fallback : value;
    };
    const proxy = useShopProxy();
    const inputRef = useRef(null);
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listUnavailable, setListUnavailable] = useState(false);
    const [uploading, setUploading] = useState(null);   // { done, total }
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        if (!proxy) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await proxy('GET', '/admin/files?prependHost=true');
            if (!aliveRef.current) return;
            if (res?.success && Array.isArray(res.data?.files)) {
                setFiles(res.data.files);
                setListUnavailable(false);
            } else {
                // older mini-api without the list route: keep working, session-only
                setListUnavailable(true);
            }
        } catch {
            if (aliveRef.current) setListUnavailable(true);
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [proxy]);

    useEffect(() => { load(); }, [load]);

    const onPicked = async (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = '';
        if (!picked.length || !proxy) return;
        setError(null);
        const tooLarge = picked.filter((f) => f.size > MAX_FILE_BYTES).map((f) => f.name);
        const todo = picked.filter((f) => f.size <= MAX_FILE_BYTES);
        const failed = [];
        const uploaded = [];
        for (let i = 0; i < todo.length; i += 1) {
            if (!aliveRef.current) return;
            setUploading({ done: i, total: todo.length });
            try {
                const dataUrl = await readAsDataUrl(todo[i]);
                const res = await proxy('POST', '/admin/upload?prependHost=true', { file_base64: dataUrl, filename: todo[i].name });
                if (res?.success && res.data?.url) {
                    uploaded.push({
                        filename: res.data.filename,
                        label:    todo[i].name,
                        size:     todo[i].size,
                        modified: new Date().toISOString(),
                        url:      res.data.url,
                    });
                } else {
                    failed.push(todo[i].name);
                }
            } catch {
                failed.push(todo[i].name);
            }
        }
        if (!aliveRef.current) return;
        setUploading(null);
        const problems = [];
        if (tooLarge.length) problems.push(`${tr('cfg_files_err_large', 'Te groot (maximaal 20 MB)')}: ${tooLarge.join(', ')}`);
        if (failed.length) problems.push(`${tr('cfg_files_err_upload', 'Niet geüpload (bestandstype niet toegelaten of serverfout)')}: ${failed.join(', ')}`);
        if (problems.length) setError(problems.join(' — '));
        if (listUnavailable) setFiles((prev) => [...uploaded, ...prev]);
        else await load();
    };

    const copyUrl = async (file) => {
        try {
            await navigator.clipboard.writeText(file.url);
        } catch {
            // clipboard API needs a secure context; fall back to a temporary field
            const el = document.createElement('textarea');
            el.value = file.url;
            document.body.appendChild(el);
            el.select();
            try { document.execCommand('copy'); } catch { /* nothing more to try */ }
            document.body.removeChild(el);
        }
        setCopied(file.filename);
        setTimeout(() => { if (aliveRef.current) setCopied((c) => (c === file.filename ? null : c)); }, 1800);
    };

    const confirmDelete = async () => {
        if (!toDelete || !proxy) return;
        setDeleting(true);
        try {
            const res = await proxy('DELETE', `/admin/files/${encodeURIComponent(toDelete.filename)}`);
            if (!aliveRef.current) return;
            if (res?.success) {
                setFiles((prev) => prev.filter((f) => f.filename !== toDelete.filename));
            } else {
                setError(res?.message || tr('cfg_files_err_delete', 'Het bestand kon niet verwijderd worden.'));
            }
        } catch {
            if (aliveRef.current) setError(tr('cfg_files_err_delete', 'Het bestand kon niet verwijderd worden.'));
        } finally {
            if (aliveRef.current) { setDeleting(false); setToDelete(null); }
        }
    };

    if (!proxy) return null;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {tr('cfg_files_title', 'Bestanden')}
                        {!loading && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({files.length})</Typography>}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_files_desc', 'Upload een bestand (bv. een pdf) en gebruik de URL in een link. De bestanden blijven hier staan, voor iedereen.')}
                    </Typography>
                </Box>
                {/* inline display:none -- a global stylesheet overrides the bare `hidden` attribute on inputs */}
                <input ref={inputRef} type="file" multiple onChange={onPicked} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
                <Button
                    size="small" variant="outlined"
                    disabled={!!uploading}
                    startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon fontSize="small" />}
                    onClick={() => inputRef.current?.click()}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                    {uploading
                        ? `${tr('cfg_files_uploading', 'Uploaden')} ${uploading.done + 1}/${uploading.total}`
                        : tr('cfg_files_upload', 'Bestand uploaden')}
                </Button>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>{error}</Alert>}
            {listUnavailable && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    {tr('cfg_files_list_unavailable', 'Deze webshop draait een oudere serverversie: uploaden werkt, maar de lijst toont alleen wat in deze sessie is geüpload. Na een webshop-update verschijnen alle bestanden.')}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
            ) : files.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    {tr('cfg_files_empty', 'Nog geen bestanden.')}
                </Typography>
            ) : (
                <Stack spacing={0.5} sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                    {files.map((file) => (
                        <Box
                            key={file.filename}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
                        >
                            <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" noWrap title={file.filename}>{file.label || file.filename}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {formatSize(file.size)}{file.modified ? ` · ${new Date(file.modified).toLocaleDateString()}` : ''}
                                </Typography>
                            </Box>
                            <Tooltip title={copied === file.filename ? tr('cfg_files_copied', 'Gekopieerd') : tr('cfg_files_copy', 'URL kopiëren')}>
                                <IconButton size="small" onClick={() => copyUrl(file)} color={copied === file.filename ? 'success' : 'default'}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {typeof onUseUrl === 'function' && (
                                <Tooltip title={tr('cfg_files_use', 'Nieuwe link maken met dit bestand')}>
                                    <IconButton size="small" onClick={() => onUseUrl(file.url, file.label || file.filename)}>
                                        <AddLinkIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={tr('cfg_files_open', 'Openen')}>
                                <IconButton size="small" component="a" href={file.url} target="_blank" rel="noopener noreferrer">
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {!listUnavailable && (
                                <Tooltip title={tr('cfg_files_delete', 'Verwijderen')}>
                                    <IconButton size="small" onClick={() => setToDelete(file)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}
                </Stack>
            )}

            <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{tr('cfg_files_delete_title', 'Bestand verwijderen?')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {tr('cfg_files_delete_body', 'Links die naar dit bestand verwijzen werken daarna niet meer.')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, wordBreak: 'break-all' }}>{toDelete?.label || toDelete?.filename}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)} disabled={deleting}>{tr('cfg_docimport_cancel', 'Annuleren')}</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={16} color="inherit" /> : tr('cfg_files_delete', 'Verwijderen')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

ShopFileLibrary.propTypes = {
    t:        PropTypes.func,
    onUseUrl: PropTypes.func,
};

export default ShopFileLibrary;
